<script setup lang="ts">
import { ElTabs, ElTabPane, type TabsPaneContext } from "element-plus";
import { watch, computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { TabNavItem } from "@/components/layout/navigation/types";
import { useI18n } from "vue-i18n";

export interface Props {
  items: Readonly<TabNavItem[]>;
  activeName?: string;
  useRouterMode?: boolean;
}
const props = withDefaults(defineProps<Props>(), { useRouterMode: false });

const emit = defineEmits<{
  (e: "click", index: number): void;
}>();

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const getTabActiveName = computed(() => {
  if (props.useRouterMode) {
    return route.name?.toString() ?? props.items[0].route;
  }
  return props.activeName ?? props.items[0].route;
});

const tabActiveName = ref(getTabActiveName.value);

const handleClick = (tab: TabsPaneContext) => {
  const selectedTabIndex = props.items.findIndex(
    (item) => item.route === tab.props.name
  );
  emit("click", selectedTabIndex);

  const tabName = tab.props.name?.toString() ?? "";
  if (props.useRouterMode) router.push({ name: tabName });
};

watch(
  () => router.currentRoute.value,
  (newRoute) => {
    tabActiveName.value = newRoute.name?.toString() ?? props.items[0].route;
  }
);
</script>

<template>
  <el-tabs
    v-model="tabActiveName"
    v-cy="'TabNavBar'"
    class="tab-container"
    @tab-click="handleClick"
  >
    <template
      v-for="item in items"
      :key="item.title"
    >
      <el-tab-pane
        :label="t(item.titleKey)"
        :name="item.route"
        :disabled="item.disabled"
      />
    </template>
  </el-tabs>
</template>

<style lang="css" scoped>
.tab-container:not(.so-tabs--border-card) {
  margin-bottom: 20px;
}

:deep(.so-tabs__item.is-active) {
  color: var(--so-color-secondary);
}

:deep(.so-tabs__item.is-disabled) {
  text-decoration: line-through;
  cursor: default;
  opacity: 0.5;
}

:deep(.so-tabs__item.is-disabled:hover) {
  color: var(--so-text-color-primary);
  opacity: 0.6;
}

:deep(.so-tabs__item:not(.is-disabled):hover) {
  color: var(--so-color-secondary);
}

:deep(.so-tabs__active-bar) {
  background-color: var(--so-color-secondary);
}
</style>
